import React, { useState } from "react"
import { Link, graphql } from "gatsby"

import SEO from "../components/common/seo"
import Layout from "../components/common/layout"
// import TopicList from "../components/topic/topiclist"
import TopicFilters from "../components/topic/filters"
import TopicStatsCard from "../components/topic/topicStatsCard"
import { TopicDescriptionLine } from "../components/topic/topicDescriptionLine"
import CommunitiesTable from "../components/community/communitiestable"
import TopicPageFAQ from "../components/topic/topicPageFaq"
import PlatformIcons from "../components/platform/platformIcons"

import { trackEvent } from "../utils/tracking"
import { sortCommunities, useCommunityListSort } from "../utils/communities"
import { buildPlatformFaqEntries } from "../components/faq/useFaqEntries"
import { SIZES } from "../constants/memberSize"
import { setLogoImgFallbackOnError } from "../utils/logoPlaceholder"
import { getSchemaForPlatform } from "../utils/schema"
import { topicListUpdatedLabel } from "../utils/topics"

const PlatformPage = ({ data, classes, pageContext }) => {
  const platform = data.platform
  const showCommunitySidebar = true

  const count = data.communities.totalCount
  const communities = data.communities.nodes.sort((a, b) =>
    a.members < b.members ? 1 : b.members < a.members ? -1 : 0
  )

  const [sizeFilters, setSizeFilters] = useState([])
  const [featureFilters, setFeatureFilters] = useState([])
  const { sort, setSort, sortOptions } = useCommunityListSort()

  // get topics that this platform covers
  var topicCounts = {} // key is slug, value is count
  communities
    .map(c => c.topics)
    .forEach(slugs => {
      const topics = slugs ? slugs.split(",") : []
      topics.forEach(t => {
        if (topicCounts[t]) {
          topicCounts[t]["count"] += 1
        } else if (data.topics.nodes.filter(topic => topic.slug === t)[0]) {
          topicCounts[t] = {
            topic: data.topics.nodes.filter(topic => topic.slug === t)[0],
            count: 1,
          }
        }
      })
    })

  const topicRowsSorted = Object.keys(topicCounts)
    .map(key => topicCounts[key])
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))
    .map(({ topic: t, count: topicCount }) => ({
      topic: t,
      count: topicCount,
      topCommunities: communities
        .filter(
          c =>
            c.topics &&
            c.topics
              .split(",")
              .map(s => s.trim())
              .includes(t.slug)
        )
        .slice(0, 3)
        .map(c => c.name),
    }))

  // get tools for this community
  var platformTools = data.allTools.nodes.filter(
    tool => tool.platforms && tool.platforms.split(",").includes(platform.slug)
  )
  platformTools = platformTools.sort((a, b) => {
    if (a.promoted && !b.promoted) return -1
    if (!a.promoted && b.promoted) return 1
    return a.order < b.order ? 1 : b.order < a.order ? -1 : 0
  })
  const [countToolsToShow, setCountToolsToShow] = useState(4)

  const updatedLabel = topicListUpdatedLabel(null, communities)

  // get the redirect url
  var urlForRedirect = platform.link
  var linkDisplay = ""
  if (urlForRedirect) {
    if (urlForRedirect.includes("?")) {
      urlForRedirect += "&ref=thehiveindex.com&utm_source=thehiveindex.com"
    } else {
      urlForRedirect += "?ref=thehiveindex.com&utm_source=thehiveindex.com"
    }
    linkDisplay = platform.link
      .replace("www.", "")
      .replace(`https://`, "")
      .replace(`http://`, "")
      .split("?")[0]
  }

  // sizes of communities
  var sizes = SIZES.map(name => ({
    name,
    count: communities.filter(c => c.memberSize === name).length,
  }))

  // filter communiites based on filter
  var filteredCommunities = communities
  if (sizeFilters.length) {
    filteredCommunities = filteredCommunities.filter(c =>
      sizeFilters.map(f => f.name).includes(c.memberSize)
    )
  }
  if (featureFilters.length) {
    filteredCommunities = filteredCommunities.filter(
      c => !featureFilters.map(f => c[f.slug]).includes(false)
    )
  }

  // sort them
  filteredCommunities = sortCommunities(sort, filteredCommunities)

  const faq = communities.length
    ? buildPlatformFaqEntries(communities, {
        platformName: platform.name,
        platformDescription: platform.description,
        platformGroupNamePlural: platform.groupNamePlural,
        totalCommunities: count,
        slug: platform.slug,
        features: data.allFeatures.nodes,
        topicRows: topicRowsSorted,
        updatedDate: updatedLabel,
        siteName: data.site.siteMetadata.title,
      }).map(entry => ({
        question: entry.question,
        answer: entry.answerElement,
        answerPlain: entry.answerPlain,
      }))
    : []

  const title = `Best ${
    platform.groupNamePlural
      ? platform.groupNamePlural
      : `${platform.name} Communities`
  } to Join`

  var description = `Discover ${count || ""} online communities on ${
    platform.name
  }`
  if (filteredCommunities.length > 0) {
    description += ", like "
    description += filteredCommunities
      .slice(0, 3)
      .map(c => c.name)
      .join(", ")
    description += " and more."
  } else {
    description += "."
  }

  return (
    <Layout
      pageTitle={title}
      shareButtons={true}
      noBackground={true}
      enableShare={true}
    >
      <SEO
        title={title}
        description={description}
        imageUrl={platform.logoBig}
        generateImage={true}
        path={`/platforms/${platform.slug}/`}
        dontIndex={Boolean(platform.removed)}
        schema={getSchemaForPlatform({
          platform,
          communities: filteredCommunities,
          topicCounts,
          title,
          description,
          faq,
        })}
      />

      {/* Main 3 column grid */}
      <div
        className={`grid grid-cols-1 gap-4 items-start lg:grid-cols-4 lg:gap-8`}
      >
        {/* Left column */}
        <div className="grid grid-cols-1 gap-4 lg:col-span-3">
          <div className="rounded-lg bg-white overflow-hidden shadow p-6">
            <div className="relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex items-center justify-between">
                <h2 className="min-w-0 pr-3 bg-white text-xl font-medium text-gray-900">
                  {count} {platform.name}{" "}
                  {count === 1 ? "Community" : "Communities"}
                </h2>
                <span className="hidden sm:inline shrink-0 pl-3 bg-white text-sm text-gray-500">
                  Updated {updatedLabel}
                </span>
              </div>
            </div>

            {platform.description && String(platform.description).trim() ? (
              <div className={count < 5 ? "mb-6 mt-2" : "mt-2"}>
                {count < 5 ? (
                  <p className="text-gray-500 leading-snug">
                    {platform.description}
                  </p>
                ) : (
                  <TopicDescriptionLine description={platform.description} />
                )}
              </div>
            ) : null}

            <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
              <div className="min-w-0 sm:mt-0 sm:mb-2">
                <PlatformIcons
                  platforms={data.allPlatforms.nodes}
                  platform={platform}
                />
              </div>

              {count > 10 ? (
                <div className="w-full min-w-0 sm:ml-auto sm:w-auto">
                  <TopicFilters
                    features={data.allFeatures.nodes.map(f => ({
                      ...f,
                      count: communities.filter(c => c[f.slug]).length,
                    }))}
                    featureFilters={featureFilters}
                    setFeatureFilters={setFeatureFilters}
                    sizes={sizes}
                    sizeFilters={sizeFilters}
                    setSizeFilters={setSizeFilters}
                    sort={sort}
                    setSort={setSort}
                    sortOptions={sortOptions}
                  />
                </div>
              ) : null}
            </div>

            {count > 0 ? (
              <div className="mt-1">
                <CommunitiesTable
                  communities={filteredCommunities}
                  showPlatform={false}
                  showTopics={true}
                  linkModal={true}
                  showRankings={true}
                />
              </div>
            ) : (
              <div className="mt-1">
                <div
                  style={{
                    padding: "20px 0 0",
                    textAlign: "center",
                    fontWeight: "500",
                    color: "#95a5a6",
                  }}
                >
                  There are no online communities on this platform yet.
                </div>
              </div>
            )}
          </div>

          {faq.length ? <TopicPageFAQ faq={faq} /> : null}
        </div>

        {/* Right column */}
        <div className="grid grid-cols-1 gap-4">
          {platform.removed || platform.descriptionForCommunities ? (
            <section aria-labelledby="platform-builders-heading">
              <div className="rounded-lg bg-white overflow-hidden shadow p-6">
                {platform.removed ? (
                  <div
                    className="mb-4 rounded-md bg-red-600 px-4 py-3 text-sm font-semibold text-white"
                    role="status"
                  >
                    This platform is no longer active.
                  </div>
                ) : null}

                {platform.descriptionForCommunities ? (
                  <React.Fragment>
                    <div className="relative">
                      <div
                        className="absolute inset-0 flex items-center"
                        aria-hidden="true"
                      >
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex items-center justify-between">
                        <h2
                          id="platform-builders-heading"
                          className="pr-3 bg-white text-xl font-medium text-gray-900"
                        >
                          {platform.name} for community builders
                        </h2>
                      </div>
                    </div>

                    <p className="mt-1 text-gray-500 whitespace-pre-wrap">
                      {platform.descriptionForCommunities}
                    </p>

                    {platform.promotionText ? (
                      <p className="mt-2 text-green-400 font-bold">
                        {platform.promotionText}
                      </p>
                    ) : null}

                    {urlForRedirect ? (
                      <div className="mt-4">
                        <a
                          href={urlForRedirect}
                          target="_blank"
                          rel="noopener"
                          onClick={() => {
                            trackEvent("PlatformRedirect", {
                              event_label: platform.name,
                              slug: platform.slug,
                              name: platform.name,
                            })
                          }}
                          className="flex justify-center items-center px-4 py-2 border border-rose-600 text-white bg-rose-500 hover:bg-rose-600 shadow-sm text-sm rounded-sm text-white"
                        >
                          <div className="mr-4">
                            <div className="opacity-100 font-medium max-w-xs truncate">
                              {linkDisplay}
                            </div>
                            <div className="opacity-75 text-xs font-semibold">
                              Go to community platform
                            </div>
                          </div>
                          <div className="ml-auto">→</div>
                        </a>
                      </div>
                    ) : null}
                  </React.Fragment>
                ) : null}
              </div>
            </section>
          ) : null}

          {count > 0 ? (
            <TopicStatsCard
              topic={null}
              platform={platform}
              platformTopicBreakdown={{
                platform,
                rows: topicRowsSorted,
              }}
              communities={communities}
              features={data.allFeatures.nodes}
              platforms={data.allPlatforms.nodes}
              sizeFilters={sizeFilters}
              setSizeFilters={setSizeFilters}
              featureFilters={featureFilters}
              setFeatureFilters={setFeatureFilters}
            />
          ) : null}

          {platformTools.length ? (
            <section aria-labelledby="platform-info">
              <div className="rounded-lg bg-white overflow-hidden shadow p-6">
                <div className="">
                  <div className="relative">
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex items-center justify-between">
                      <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
                        {platform.name} Community Tools
                      </h2>
                      <span className="text-sm ml-4 pl-2 font-semibold bg-white">
                        <span className="opacity-50">
                          {platformTools.length}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="mt-1 text-gray-500">
                    <div className={`mt-2 space-y-4`}>
                      {platformTools
                        .filter((t, i) => i < countToolsToShow)
                        .map((t, index) => (
                          <a
                            className="flex"
                            key={t.slug}
                            href={t.link}
                            target="_blank"
                            rel="nofollow"
                            onClick={() => {
                              // track event
                              trackEvent("ToolClick", {
                                event_label: t.name,
                                slug: t.slug,
                                name: t.name,
                                page: "ToolsList",
                                index: index,
                              })
                            }}
                          >
                            {t.logo ? (
                              <div className="mr-3 h-12 w-12 flex-shrink-0">
                                <img
                                  src={t.logo}
                                  alt=""
                                  className="h-12 w-12 rounded-md mr-3 object-contain"
                                  onError={setLogoImgFallbackOnError}
                                />
                              </div>
                            ) : (
                              ""
                            )}

                            <div>
                              <div
                                className={`block cursor-pointer font-medium text-rose-500 hover:underline`}
                              >
                                {t.name}

                                {t.promoted ? (
                                  <span className="ml-2 text-xs bg-yellow-400 font-semibold text-white rounded-md px-2 py-0.5">
                                    Featured
                                  </span>
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="mt-0 block text-sm text-gray-500 line-clamp-2">
                                {t.headline}
                              </div>
                            </div>
                          </a>
                        ))}
                    </div>

                    {platformTools.length > countToolsToShow ? (
                      <div className="pt-4 mx-auto text-center">
                        <div
                          onClick={() =>
                            setCountToolsToShow(platformTools.length)
                          }
                          className="mx-auto text-gray-600 font-semibold text-xs opacity-75 hover:opacity-100 cursor-pointer"
                        >
                          More Tools for {platform.name} →
                        </div>
                      </div>
                    ) : (
                      ""
                    )}

                    <div className="pt-2 mx-auto text-center">
                      <Link
                        to={`/tools/`}
                        className="mx-auto text-gray-600 font-semibold text-xs opacity-75 hover:opacity-100 cursor-pointer"
                      >
                        All Community Tools →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            ""
          )}
        </div>
      </div>
    </Layout>
  )
}

export const platformPageQuery = graphql`
  query PlatformPage($slug: String, $communitySlugs: [String]) {
    site {
      siteMetadata {
        title
      }
    }
    platform: googleSpreadsheetSourcePlatforms(slug: { eq: $slug }) {
      name
      slug
      count
      logoSmall
      logoBig
      groupName
      groupNamePlural
      description
      descriptionForCommunities
      link
      promoted
      removed
    }
    communities: allGoogleSpreadsheetSourceCommunities(
      filter: {
        slug: { in: $communitySlugs }
        removed: { ne: true }
        suppress: { ne: true }
      }
      sort: { fields: members, order: DESC }
    ) {
      totalCount
      nodes {
        name
        slug
        members
        memberSize
        countFeatures
        order
        established
        platform
        description
        topics
        logo
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        reviewCount
        reviewAvg
        reviewsPositive
        views
        visitors
        redirects
        viewsPrev
        redirectsPrev
        gold
        sponsored
        claimed
        added
        updated
      }
    }
    topics: allGoogleSpreadsheetSourceTopics(filter: { count: { gt: 0 } }) {
      nodes {
        slug
        name
        count
        image
        people
        views
        viewsWithCommunities
      }
    }
    allPlatforms: allGoogleSpreadsheetSourcePlatforms(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        count
        logoBig
        logoSmall
        groupName
        groupNamePlural
        promoted
        removed
        descriptionForCommunities
      }
    }
    allTools: allGoogleSpreadsheetSourceTools(
      filter: { removed: { ne: true } }
    ) {
      nodes {
        order
        slug
        name
        headline
        categories
        platforms
        logo
        link
        promoted
      }
    }
    allFeatures: allGoogleSpreadsheetSourceFeatures(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        icon
      }
    }
  }
`

export default PlatformPage
